// Fiche d'un relevé : synthèse, lignes du relevé et écarts côté système, rapprochement manuel
// motivé d'une ligne en écart avec un paiement confirmé non encore rapproché.
import { Bandeau, LienBouton, Lien, Panneau, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { LIBELLES_CANAL, LIBELLES_ETAT_LIGNE, LIBELLES_ORIGINE_LIGNE, francs, libelle, type LigneRapprochement } from './donnees';
import { FormulaireAction } from './FormulaireAction';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { puceReleve } from './PageRapprochements';
import { usePaiements, useReleve } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

export function PageReleve() {
  const { id = '' } = useParams();
  const droits = useDroitsRecettes();
  const fiche = useReleve(id);
  const r = fiche.data;
  const confirmes = usePaiements({ etat: 'confirme', canal: r?.canal ?? '', en_anomalie: '' }, Boolean(r) && droits.modifier);
  const dejaRapproches = new Set((r?.lignes ?? []).filter((l) => l.paiement_id && l.etat !== 'ecart').map((l) => l.paiement_id));
  const candidats = (confirmes.data ?? []).filter((p) => !dejaRapproches.has(p.id)).map((p) => ({ valeur: p.id, libelle: `${p.reference_serveur}, ${francs(p.montant)}, ${dateHeure(p.confirme_le)}` }));

  const colonnes: ColonneTableau<LigneRapprochement>[] = [
    { id: 'origine', entete: 'Origine', enTeteLigne: true, masquable: false, valeurTri: (l) => `${l.origine}-${l.rang}`, cellule: (l) => `${libelle(LIBELLES_ORIGINE_LIGNE, l.origine)}, ligne ${l.rang}` },
    { id: 'reference', entete: 'Référence', valeurTri: (l) => l.reference ?? null, cellule: (l) => <span className={styles.mono}>{l.reference ?? 'absente'}</span> },
    { id: 'montant', entete: 'Montant', numerique: true, valeurTri: (l) => l.montant ?? null, cellule: (l) => <span className={styles.montant}>{francs(l.montant)}</span> },
    { id: 'date', entete: 'Date', valeurTri: (l) => l.date ?? null, cellule: (l) => date(l.date) },
    { id: 'paiement', entete: 'Paiement', cellule: (l) => (l.paiement_id ? <Lien href={`/recettes/paiements/${l.paiement_id}`} className={styles.mono}>{l.paiement_reference ?? l.paiement_id.slice(0, 8)}</Lien> : 'aucun') },
    { id: 'etat', entete: 'État', valeurTri: (l) => l.etat, cellule: (l) => (
      <>
        <PuceEtat ton={l.etat === 'ecart' ? 'retard' : 'ok'} libelle={libelle(LIBELLES_ETAT_LIGNE, l.etat)} />
        {l.motif ? <div className={styles.sous}>{l.motif}</div> : null}
      </>
    ) },
    { id: 'actions', entete: 'Actions', masquable: false, cellule: (l) => (l.etat === 'ecart' && l.origine === 'releve' && droits.modifier ? (
      <FormulaireAction
        libelle="Rapprocher"
        titre={`Rapprocher la ligne ${l.rang} du relevé`}
        explication={`Ligne ${l.reference ?? 'sans référence'} de ${francs(l.montant)} : choisissez le paiement confirmé correspondant ; le motif est journalisé et l'écart côté système disparaît.`}
        chemin={`/recettes/releves/${id}/lignes/${l.id}/rapprocher`}
        champs={[
          candidats.length > 0 ? { nom: 'paiement_id', libelle: 'Paiement confirmé', type: 'selection', obligatoire: true, options: candidats } : { nom: 'paiement_id', libelle: 'Identifiant du paiement confirmé', obligatoire: true },
          { nom: 'motif', libelle: 'Motif', type: 'multiligne', obligatoire: true },
        ]}
        succes="Ligne rapprochée."
        variante="secondaire"
        petit
      />
    ) : null) },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Finances, relevé"
        titre={r ? `Relevé ${libelle(LIBELLES_CANAL, r.canal)} du ${date(r.periode.debut)}${r.periode.debut !== r.periode.fin ? ` au ${date(r.periode.fin)}` : ''}` : 'Relevé'}
        description={r ? <span className={commun.ligne}>{puceReleve(r.synthese)}</span> : null}
        actions={<LienBouton href="/recettes/rapprochements" icone={ArrowLeft}>Rapprochements</LienBouton>}
      />
      <NavigationFinances />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L2-A" fonction="La fiche du relevé">
        {r ? (
          <div className={commun.pile}>
            <Panneau titre="Synthèse">
              <dl className={styles.segments}>
                <div>
                  <dt>Lignes du relevé</dt>
                  <dd className={styles.grand} data-testid="synthese-lignes">{r.synthese.lignes}</dd>
                  <dd className={styles.petit}>{r.synthese.auto} automatiques, {r.synthese.manuel} manuelles</dd>
                </div>
                <div>
                  <dt>Écarts</dt>
                  <dd className={styles.grand} data-testid="synthese-ecarts">{r.synthese.ecarts}</dd>
                  <dd className={styles.petit}>dont {r.synthese.absents_du_releve} paiement(s) absent(s) du relevé</dd>
                </div>
                <div>
                  <dt>Total du relevé</dt>
                  <dd className={styles.grand}>{francs(r.synthese.total_releve)}</dd>
                  <dd className={styles.petit}>rapproché : {francs(r.synthese.total_rapproche)}</dd>
                </div>
                <div>
                  <dt>Importé le</dt>
                  <dd>{dateHeure(r.importe_le)}</dd>
                  <dd className={styles.petit}>{r.prestataire ? `prestataire ${r.prestataire}` : 'tous prestataires'}</dd>
                </div>
              </dl>
              {r.synthese.integral ? <Bandeau type="succes">Relevé intégralement rapproché.</Bandeau> : <Bandeau type="attention">Des écarts restent à instruire ; une anomalie d&apos;écart de rapprochement est ouverte.</Bandeau>}
            </Panneau>
            <Panneau titre="Lignes">
              <Tableau legende="Lignes du relevé" colonnes={colonnes} lignes={r.lignes} cleLigne={(l) => l.id} />
            </Panneau>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
